<template>
    <page-container class="grid grid-cols-12 gap-8">
        <settings-sidebar></settings-sidebar>
        <div class="col-span-12 md:col-span-8 lg:col-span-10">
            <div v-if="integration" class="max-w-(--breakpoint-sm) flex flex-col gap-4">
                <page-header>
                    <div class="flex items-center gap-3">
                        <a
                            v-if="integration.url"
                            :href="integration.url"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="size-9 flex items-center justify-center shrink-0 rounded shadow hover:shadow-md transition-shadow"
                            :class="integration.logo ? 'bg-slate-50' : 'bg-sky-400'"
                        >
                            <component
                                :is="integration.logo"
                                v-if="integration.logo"
                                class="size-7 max-h-full max-w-full"
                            />
                            <span v-else class="text-white text-base font-semibold">{{
                                integration.name.charAt(0).toUpperCase()
                            }}</span>
                        </a>
                        <template v-else>
                            <div
                                v-if="integration.logo"
                                class="size-9 flex items-center justify-center shrink-0 bg-slate-50 rounded shadow"
                            >
                                <component :is="integration.logo" class="size-7 max-h-full max-w-full" />
                            </div>
                            <div
                                v-else
                                class="size-9 shrink-0 bg-sky-400 rounded shadow flex items-center justify-center text-white text-base font-semibold"
                            >
                                {{ integration.name.charAt(0).toUpperCase() }}
                            </div>
                        </template>
                        <span>{{ integration.name }}</span>
                    </div>
                    <template v-slot:subtitle>
                        <a href="/settings/relay" class="no-underline! text-sky-600! text-base font-normal">
                            <font-awesome-icon widthAuto class="mr-1" :icon="faArrowLeft" />
                            Voxie Relay
                        </a>
                    </template>
                    <template v-slot:actions>
                        <vx-badge v-if="isEnabled" color="success">Enabled</vx-badge>
                    </template>
                </page-header>

                <vx-box-glass class="text-slate-700 font-sans">
                    <vx-wallpaper :icon="faCircleNodes"></vx-wallpaper>
                    <div class="relative p-6 xl:p-10 max-w-2xl">
                        <h2 class="mb-2 text-xl font-bold">
                            Overview
                            <font-awesome-icon widthAuto :icon="faTelescope"></font-awesome-icon>
                        </h2>
                        <p class="text-sm mb-3 font-sans text-slate-700 lg:text-base">{{ integration.overview }}</p>
                    </div>
                </vx-box-glass>

                <vx-expandable-card>
                    <template v-slot:header>Features</template>
                    <template v-slot:content>
                        <ul class="list-disc list-inside text-slate-700 space-y-2">
                            <li v-for="(feature, index) in integration.features" :key="index">
                                {{ feature }}
                            </li>
                        </ul>
                    </template>
                </vx-expandable-card>

                <vx-expandable-card>
                    <template v-slot:header>Get Started</template>
                    <template v-slot:content>
                        <div v-if="isEnabled && integration.settingsPath" class="flex flex-col gap-4">
                            <p class="text-slate-700">
                                This integration is active on your account. Visit the settings page to configure it.
                            </p>
                            <vx-button size="lg" :href="integration.settingsPath">Go to Settings</vx-button>
                        </div>
                        <div v-else class="flex flex-col gap-4">
                            <p class="text-slate-700">Contact Voxie Support to learn more about this integration.</p>
                            <vx-button size="lg" :href="supportEmailHref">Contact Support</vx-button>
                        </div>
                    </template>
                </vx-expandable-card>
            </div>
            <div v-else class="flex flex-col items-center justify-center py-20 text-slate-500">
                <p class="text-xl">Integration not found</p>
                <a href="/settings/relay" class="text-primary-600 mt-4">Back to Voxie Relay</a>
            </div>
        </div>
    </page-container>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { VxExpandableCard, VxBadge, VxButton, VxBoxGlass, VxWallpaper } from '@voxie/frontend-components';
import PageContainer from '~/components/general/PageContainer.vue';
import PageHeader from '~/components/general/PageHeader.vue';
import SettingsSidebar from '~/components/settings/menus/SettingsSidebar.vue';
import { getIntegrationBySlug } from './integrations';
import { useIntegrationEnabled } from './useIntegrationEnabled';
import { faCircleNodes, faTelescope } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faArrowLeft } from '@fortawesome/pro-solid-svg-icons';

const props = defineProps<{
    integration: string;
}>();

const { isEnabled: checkEnabled } = useIntegrationEnabled();
const teamName = Spark.state.currentTeam.name;

const integration = computed(() => getIntegrationBySlug(props.integration));

const isEnabled = computed(() => {
    if (!integration.value) {
        return false;
    }
    return checkEnabled(integration.value);
});

const supportEmailHref = computed(() => {
    const subject = encodeURIComponent(`Learn more about ${integration.value?.name} for ${teamName}`);
    return `mailto:support@voxie.com?subject=${subject}`;
});
</script>
